import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useEffect } from 'react';

export default function AboutPage() {
  useEffect(() => {
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Exalt Ventures Ltd.",
      "description": "A distinguished master franchise operator delivering excellence in retail across North Africa and Malta since 2007.",
      "url": `${import.meta.env.VITE_SITE_URL || "https://exaltventures.com"}/about`,
      "mainEntity": {
        "@type": "Organization",
        "name": "Exalt Ventures Ltd.",
        "foundingDate": "2007",
        "description": "Master franchise and retail operating group managing international fashion brands across Libya, Algeria, and Malta.",
        "areaServed": ["Libya", "Algeria", "Malta"]
      }
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-serif text-[#003264] mb-6 leading-tight">About Exalt</h1>
            <p className="text-base text-[#003264] leading-relaxed">
              A distinguished master franchise operator delivering excellence in retail across North Africa and Malta since 2007.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white p-12">
              <h2 className="text-3xl font-serif text-[#003264] mb-6">Our Story</h2>
              <div className="space-y-5 text-sm text-[#003264] leading-relaxed">
                <p>
                  Exalt Ventures Ltd. was incorporated in 2007 with a vision to bring world-class international fashion brands to the discerning markets of North Africa and Malta. Over the years, we have established ourselves as a trusted master franchise owner and retail operator, known for our commitment to excellence and sophisticated retail experiences.
                </p>
                <p>
                  Our journey began with a deep understanding of the regional market dynamics and a passion for delivering premium fashion to customers who appreciate quality, style, and authenticity. Today, we operate multiple retail locations across Libya, Algeria, and Malta, each carefully curated to reflect the heritage and values of the brands we represent.
                </p>
                <p>
                  Through strategic partnerships with renowned British fashion houses, we have built a portfolio that spans women's fashion, children's apparel, premium footwear, handbags, accessories, and fine jewellery. Our success is rooted in our ability to adapt global fashion trends to local preferences while maintaining the integrity and prestige of each brand.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Categories */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-serif text-[#003264] text-center mb-12">Brand Categories</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[#FAFAFA] p-8 hover:shadow-lg transition-shadow duration-300">
                <div className="w-12 h-12 flex items-center justify-center mb-5">
                  <i className="ri-user-3-line text-3xl text-[#64C8FA]"></i>
                </div>
                <h3 className="text-xl font-serif text-[#003264] mb-3">Women's Fashion</h3>
                <p className="text-sm text-[#003264] leading-relaxed">
                  Curated collections of sophisticated apparel and accessories for the modern woman who values elegance and quality.
                </p>
              </div>
              <div className="bg-[#FAFAFA] p-8 hover:shadow-lg transition-shadow duration-300">
                <div className="w-12 h-12 flex items-center justify-center mb-5">
                  <i className="ri-bear-smile-line text-3xl text-[#64C8FA]"></i>
                </div>
                <h3 className="text-xl font-serif text-[#003264] mb-3">Children's Fashion</h3>
                <p className="text-sm text-[#003264] leading-relaxed">
                  Delightful and stylish clothing for children, combining comfort with timeless design and exceptional craftsmanship.
                </p>
              </div>
              <div className="bg-[#FAFAFA] p-8 hover:shadow-lg transition-shadow duration-300">
                <div className="w-12 h-12 flex items-center justify-center mb-5">
                  <i className="ri-footprint-line text-3xl text-[#64C8FA]"></i>
                </div>
                <h3 className="text-xl font-serif text-[#003264] mb-3">Premium Footwear</h3>
                <p className="text-sm text-[#003264] leading-relaxed">
                  Luxury footwear for men and women, featuring contemporary designs and superior materials from renowned British brands.
                </p>
              </div>
              <div className="bg-[#FAFAFA] p-8 hover:shadow-lg transition-shadow duration-300">
                <div className="w-12 h-12 flex items-center justify-center mb-5">
                  <i className="ri-handbag-line text-3xl text-[#64C8FA]"></i>
                </div>
                <h3 className="text-xl font-serif text-[#003264] mb-3">Handbags & Accessories</h3>
                <p className="text-sm text-[#003264] leading-relaxed">
                  Exquisite handbags, accessories, and jewellery that add the perfect finishing touch to any ensemble.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Role */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-serif text-[#003264] text-center mb-12">Our Corporate Role</h2>
            <div className="bg-white p-12">
              <div className="grid md:grid-cols-2 gap-10">
                <div>
                  <h3 className="text-xl font-serif text-[#003264] mb-4">Franchise Deployment</h3>
                  <p className="text-sm text-[#003264] leading-relaxed mb-6">
                    We specialize in the strategic deployment of master franchises, ensuring each brand is positioned for success in our markets. Our expertise in franchise management ensures seamless operations and brand consistency.
                  </p>
                  <h3 className="text-xl font-serif text-[#003264] mb-4">Store Rollout</h3>
                  <p className="text-sm text-[#003264] leading-relaxed">
                    From site selection to store design and launch, we manage every aspect of the retail rollout process. Our stores are carefully designed to reflect the luxury and sophistication of the brands we represent.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-serif text-[#003264] mb-4">Retail Operations</h3>
                  <p className="text-sm text-[#003264] leading-relaxed mb-6">
                    Our operational excellence ensures that every customer interaction reflects the premium nature of our brands. We maintain the highest standards in customer service, merchandising, and store presentation.
                  </p>
                  <h3 className="text-xl font-serif text-[#003264] mb-4">Regional Brand Management</h3>
                  <p className="text-sm text-[#003264] leading-relaxed">
                    As regional brand custodians, we protect and enhance brand equity through strategic marketing, community engagement, and consistent delivery of exceptional retail experiences.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl font-serif text-[#003264] mb-12">Our Values</h2>
            <div className="grid md:grid-cols-3 gap-10">
              <div>
                <h3 className="text-xl font-serif text-[#003264] mb-3">Excellence</h3>
                <p className="text-sm text-[#003264] leading-relaxed">
                  We pursue excellence in every aspect of our operations, from store design to customer service.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-serif text-[#003264] mb-3">Integrity</h3>
                <p className="text-sm text-[#003264] leading-relaxed">
                  We operate with transparency and honesty, building trust with our partners and customers.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-serif text-[#003264] mb-3">Innovation</h3>
                <p className="text-sm text-[#003264] leading-relaxed">
                  We continuously evolve our retail strategies to meet changing customer expectations and market dynamics.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
